import { FormEvent, MouseEvent } from 'react';
import useStyles from './styles';
import InputBase, { InputBaseProps } from '@mui/material/InputBase';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import * as IconMUI from '@mui/icons-material';

/**
 * TitleSearchbar component provides a large, bold, uppercase search input field.
 * Used in Directory layout for searching the creative directory.
 * Shows search icon and optional clear button. Form submission is triggered by Enter key.
 *
 * @param props - Component props extending InputBaseProps
 * @param props.handleSubmit - Callback function called when form is submitted
 * @param props.onClear - Optional callback function called when clear button is clicked
 * @param props.rest - Additional InputBase props (value, onChange, placeholder, etc.)
 * @returns TitleSearchbar component
 */
const TitleSearchbar = ({
  handleSubmit,
  onClear,
  inputProps: inputPropsProp,
  ...rest
}: TitleSearchbarPropTypes) => {
  const { classes } = useStyles();

  return (
    <form
      onSubmit={handleSubmit}
      className={classes.inputStyling}
      data-component="TitleSearchbar"
      autoComplete="off"
    >
      <InputBase
        {...rest}
        autoComplete="off"
        className={classes.inputStyling}
        inputProps={{
          className: classes.inputStyling,
          'aria-label': 'Search creative directory',
          name: 'search',
          autoComplete: 'off',
          ...inputPropsProp,
        }}
        fullWidth
        startAdornment={
          <InputAdornment position="start">
            <IconMUI.Search className={classes.searchIcon} aria-hidden="true" />
          </InputAdornment>
        }
        endAdornment={
          onClear ? (
            <InputAdornment position="end">
              <IconButton
                className={classes.clearButton}
                onClick={onClear}
                size="large"
                aria-label="Clear search"
              >
                <IconMUI.Clear className={classes.clearIcon} />
              </IconButton>
            </InputAdornment>
          ) : null
        }
      />
    </form>
  );
};

export interface TitleSearchbarPropTypes extends InputBaseProps {
  /** Callback function called when form is submitted */
  handleSubmit: (event: FormEvent<HTMLFormElement>) => void;
  /** Optional callback function called when clear button is clicked */
  onClear?: (event: MouseEvent<HTMLButtonElement>) => void;
}

export default TitleSearchbar;
